import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {ResourceAllocationTimeWindowExtModel} from "../../../models/resource-allocation-time-window-ext.model";

require('./resources-table-list-row.scss');
@Component({
  standalone: false,
  selector: 'app-resources-table-list-row',
  templateUrl: './resources-table-list-row.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourcesTableListRowComponent implements OnInit, OnChanges {

  @Input() resource: ResourceAllocationTimeWindowExtModel;
  @Input() settings: any;
  @Input() isSelected: boolean;
  @Input() allPanelIds: string[] = [];
  @Input() panelId: string;
  @Input() isSummaryRow: boolean = false;

  @Output() onToggleSelection = new EventEmitter<{ id: string, isSelected: boolean }>();
  @Output() onDrop = new EventEmitter<{ resource: ResourceAllocationTimeWindowExtModel, event: any }>();
  @Output() onRowClick = new EventEmitter<{ resource: ResourceAllocationTimeWindowExtModel, event: any }>();
  @Output() onRowDoubleClick = new EventEmitter<{ resource: ResourceAllocationTimeWindowExtModel, event: any }>();

  public dropListId: string;
  public routeNames: string[] = [];
  public totalDuration: number = 0;
  public totalIncome: number = 0;

  public volumeCapacity: number;
  public weightSeverity: string;
  public volumeSeverity: string;
  public palletsSeverity: string;

  public weightUnit: string;
  public volumeUnit: string;
  public severityThresholds: { low: number; mid: number; } = { low: 80, mid: 99 };

  constructor(
    @Inject('splitService') private splitService: any) {
    this.weightUnit = this.splitService.getShortWeightUnit();
    this.volumeUnit = this.splitService.getVolumeUnit();
  }

  ngOnInit() {
    this._precomputeHelpers();
    this._setDropListId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resource) {
      this._precomputeHelpers();
    }
    if (changes.resource || changes.panelId || changes.isSummaryRow) {
      this._setDropListId();
    }
  }

  // The summary row renders the same resource as its row in the list - without its own id the
  // two CdkDropLists collide and CDK resolves connected ids to whichever registered first.
  private _setDropListId() {
    this.dropListId = this.isSummaryRow ?
      `${this.panelId}-summary-${this.resource?.id}` :
      `${this.panelId}-${this.resource?.id}`;
  }

  private _precomputeHelpers() {
    if (this.resource) {
      this.routeNames = (this.resource.routes || []).map(route => route.name || route.id);
      this.totalDuration = this.resource.routes?.reduce((acc, route) => acc + route.totalTimeSec, 0) || 0;
      this.totalIncome = this.resource.routes?.reduce((acc, route: any) => acc + (route.totals?.totalIncome || 0), 0) || 0;

      // capacity and severities
      const maxLoad = this.resource.resourceAllocation?.capacity;
      const dimensions = maxLoad?.dimensions;
      const volCap = dimensions?.cube ?? ((dimensions?.length ?? 0) * (dimensions?.width ?? 0) * (dimensions?.height ?? 0));
      this.volumeCapacity = volCap;

      const currentWeight = this.resource.capacity?.weight ?? 0;
      const maxWeight = maxLoad?.weight ?? 0;
      this.weightSeverity = this._getSeverity(currentWeight, maxWeight);

      const currentVolume = this.resource.capacity?.dimensions?.cube ?? 0;
      this.volumeSeverity = this._getSeverity(currentVolume, volCap);

      const currentPallets = this.resource.capacity?.numberOfPallets ?? 0;
      const maxPallets = maxLoad?.numberOfPallets ?? 0;
      this.palletsSeverity = this._getSeverity(currentPallets, maxPallets);
    } else {
      this.routeNames = [];
      this.totalDuration = 0;
      this.totalIncome = 0;
      this.volumeCapacity = 0;
      this.weightSeverity = '';
      this.volumeSeverity = '';
      this.palletsSeverity = '';
    }
  }

  private _getSeverity(value: number, capacity?: number): string {
    if (!capacity) {
      return '';
    }
    const ratio = (value / capacity) * 100;
    return (ratio < this.severityThresholds.low) ? 'low' : ((ratio >= this.severityThresholds.low && ratio < this.severityThresholds.mid) ? 'mid' : 'high');
  }
}
